"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Slim top progress bar for route transitions. Starts on the `rb:nav-start`
 * event (dispatched from instrumentation-client's onRouterTransitionStart),
 * creeps while the destination's server components render, and completes when
 * the URL actually changes. Purely presentational — never blocks navigation.
 *
 * The element stays mounted so width/opacity changes animate: idle resets
 * instantly (transition: none), active creeps 0→80% over 8s, done snaps to
 * 100% and fades.
 */
export function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // One string rather than two deps: `searchParams` is a fresh object on every
  // render, so comparing it by identity would fire on renders where the URL
  // never moved.
  const url = `${pathname}?${searchParams}`;

  const [state, setState] = useState<"idle" | "active" | "done">("idle");
  const [seenUrl, setSeenUrl] = useState(url);
  const runId = useRef(0);

  useEffect(() => {
    function start() {
      runId.current += 1;
      const id = runId.current;
      setState("active");
      // Safety valve: a same-URL navigation never changes pathname, so an
      // active bar with no completion signal finishes itself.
      setTimeout(() => {
        if (runId.current === id) setState((s) => (s === "active" ? "done" : s));
      }, 8000);
    }
    window.addEventListener("rb:nav-start", start);
    return () => window.removeEventListener("rb:nav-start", start);
  }, []);

  // The URL moved, so the destination rendered: finish and fade. Adjusted
  // during render rather than in an effect — React re-runs this component
  // before committing, so the bar never paints a frame in the stale state.
  // The same update from an effect would commit, paint, then re-render.
  if (url !== seenUrl) {
    setSeenUrl(url);
    if (state === "active") setState("done");
  }

  useEffect(() => {
    if (state !== "done") return;
    const t = setTimeout(() => setState("idle"), 350);
    return () => clearTimeout(t);
  }, [state]);

  const style: React.CSSProperties =
    state === "active"
      ? { width: "80%", opacity: 1, transition: "width 8s cubic-bezier(.1,.6,.2,1), opacity .1s" }
      : state === "done"
        ? { width: "100%", opacity: 0, transition: "width .2s ease-out, opacity .3s ease-out .15s" }
        : { width: "0%", opacity: 0, transition: "none" };

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]" aria-hidden>
      <div
        className="h-full rounded-r-full"
        style={{ background: "var(--copper)", boxShadow: "0 0 8px var(--copper)", ...style }}
      />
    </div>
  );
}
